import { useTranslation } from 'react-i18next'
import type { ReconciliationLine, ReconciliationSession } from '../types'
import { XmlPanel } from './XmlPanel'
import { PriceListPanel } from './PriceListPanel'

type DocType = 'ordrsp' | 'desadv' | 'invoice'

interface Props {
  session: ReconciliationSession
  selectedLine?: ReconciliationLine | null
  headerXpaths?: Partial<Record<DocType, string>>
  headerLabel?: string
  onClose: () => void
  sidePanel?: boolean
}

export function XmlViewer({ session, selectedLine, headerXpaths, headerLabel, onClose, sidePanel }: Props) {
  const { t } = useTranslation()

  const availableDocs: DocType[] = (
    [
      session.ordrsp_filename  ? 'ordrsp'  : null,
      session.desadv_filename  ? 'desadv'  : null,
      session.invoice_filename ? 'invoice' : null,
    ] as (DocType | null)[]
  ).filter((t): t is DocType => t !== null)

  const xpaths: Record<DocType, string | null> = selectedLine
    ? {
        ordrsp:  selectedLine.ordrsp_xpath  ?? null,
        desadv:  selectedLine.desadv_xpath  ?? null,
        invoice: selectedLine.invoice_xpath ?? null,
      }
    : {
        ordrsp:  headerXpaths?.ordrsp  ?? null,
        desadv:  headerXpaths?.desadv  ?? null,
        invoice: headerXpaths?.invoice ?? null,
      }

  const scrollTrigger = selectedLine?.id ?? JSON.stringify(headerXpaths)

  const label = selectedLine
    ? (selectedLine.article_number.startsWith('[?') ? t('xml_viewer.no_article_number') : selectedLine.article_number)
    : (headerLabel ?? t('xml_viewer.field_label'))

  const xmlPanelMaxHeight = sidePanel ? 'calc(100vh - 220px)' : '420px'

  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">XML — {label}</span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-1" title={t('common.close')}>×</button>
      </div>
      <div className="flex divide-x divide-gray-200">
        {availableDocs.map(docType => (
          <XmlPanel
            key={docType}
            sessionId={session.id}
            docType={docType}
            xpath={xpaths[docType]}
            scrollTrigger={scrollTrigger}
            maxHeight={xmlPanelMaxHeight}
          />
        ))}
      </div>
      {selectedLine && <PriceListPanel line={selectedLine} />}
    </div>
  )
}
